import { useEffect, useRef, useState } from 'react'
import { BellRing, Camera, LayoutDashboard, Lock, Megaphone, RefreshCw, RotateCw, Trash2, Wifi } from 'lucide-react'
import { Shell, useShellMeta } from '../admin/Shell.js'
import { apiRead, Select, usePermissions, useToast } from '../admin/primitives.js'
import { SegmentedControl } from '../admin/pickers.js'
import { Field, TextInput } from '../admin/form.js'
import { useConfirm } from '../shell/confirm.js'
import { Link, navigate } from '../router.js'
import { AnnonceModale } from './AnnonceModale.js'
import {
  barresSignal, batterie, CHEMIN_TABLETTES, dashboardsDe, dashboardsPour, duree, enMaintenance, estDocFiche, etatTablette, heure, ilYa, libelleBlocage,
  libelleSecurite, lignesWifi, nomDe, profilPour, profilsWifiDe, SALLES, scanWifiDe, STYLES_BLOCAGE, temperature, urlCapture, wifi,
  type DashboardHa, type Geste, type ProfilWifi, type ScanWifi, type StyleBlocage, type Tablette,
} from './tablettes-modele.js'
import { geste, useLectureTablettes } from './tablettes-api.js'
import './tablettes.css'

/**
 * TECHNIQUE › TABLETTES › FICHE — /technique/tablettes/:id (02/11/2026).
 *
 * Grande capture, état détaillé, réglages (écran, luminosité, volume,
 * dashboard choisi dans la liste de Home Assistant), hors service,
 * maintenance, Wi-Fi (balayer, apprendre un profil, oublier un réseau),
 * mise à jour, nom et salle, retrait — et le journal des gestes
 * faits depuis Gestion. Chaque contrôle n'apparaît qu'avec son droit ; le
 * serveur vérifie de toute façon droit et confirmation forte.
 */

export function TablettePage({ id }: { id: string }) {
  const meta = useShellMeta()
  const { permissions } = usePermissions()
  const peut = {
    piloter: permissions.includes('tablettes.pilot'),
    gerer: permissions.includes('tablettes.manage'),
    enroler: permissions.includes('tablettes.enroll'),
    wifi: permissions.includes('tablettes.wifi'),
  }
  const chemin = `/api/v1/tablettes/${encodeURIComponent(id)}`
  const { lecture, precedente, recharger } = useLectureTablettes(chemin, estDocFiche, 5_000)
  const doc = lecture?.etat === 'ok' ? lecture.data : precedente
  const t = doc?.tablette ?? null
  const [journal, setJournal] = useState<Geste[] | null>(null)
  const [annonce, setAnnonce] = useState(false)
  const [tic, setTic] = useState(0)
  useEffect(() => {
    let vivant = true
    void apiRead<{ gestes?: Geste[] }>(`${chemin}/journal`).then((r) => { if (vivant && r.status === 200) setJournal(r.data.gestes ?? []) }).catch(() => undefined)
    return () => { vivant = false }
  }, [chemin, tic])
  /** Après un geste : l'état et le journal sont relus, puis encore une fois quand la tablette a eu le temps d'agir. */
  const apres = () => { recharger(); setTic((n) => n + 1); setTimeout(() => { recharger(); setTic((n) => n + 1) }, 2500) }

  if (!t) {
    return (
      <Shell active="technique" meta={meta}>
        <main className="lc-page lc-page--wide tb-page">
          <Link className="lc-crumb" href={CHEMIN_TABLETTES}>← Tablettes</Link>
          {lecture?.etat === 'erreur' ? <p className="tb-bandeau tb-bandeau--fort" role="status">{lecture.message}</p> : <p className="lc-hint" aria-busy="true">Lecture de la tablette…</p>}
        </main>
      </Shell>
    )
  }
  const e = etatTablette(t)
  return (
    <Shell active="technique" meta={meta}>
      <main className="lc-page lc-page--wide lc-stagger tb-page">
        <header className="tb-tete">
          <div className="tb-tete__titre">
            <Link className="lc-crumb" href={CHEMIN_TABLETTES}>← Tablettes</Link>
            <div className="tb-tete__ligne">
              <h1 className="lc-title">{nomDe(t)}</h1>
              {t.salle ? <span className="tb-salle tb-salle--grande">{t.salle}</span> : null}
              <span className={`tb-etat tb-ton--${e.ton}`}><span className="tb-point" aria-hidden="true" />{e.texte}</span>
            </div>
            <p className="tb-mono tb-gris">{t.id} · app {t.etat?.app ?? '—'} · Android {t.etat?.android ?? '—'} · vu {ilYa(t.vu_le)}</p>
          </div>
          {peut.piloter || peut.gerer ? <Ordres t={t} peut={peut} onFait={apres} onAnnonce={() => setAnnonce(true)} /> : null}
        </header>
        {lecture?.etat === 'erreur' ? <p className="tb-bandeau" role="status">{lecture.message} Dernier état connu affiché.</p> : null}

        <div className="tb-fiche">
          <section className="tb-fiche__principal" aria-label="Écran de la tablette">
            <Capture t={t} peutPiloter={peut.piloter} onDemande={apres} />
            <section className="lc-panel tb-panneau" aria-labelledby="tb-journal">
              <h2 className="tb-h2" id="tb-journal">Journal de la tablette</h2>
              {journal === null ? <p className="lc-hint">Lecture…</p> : journal.length === 0 ? <p className="lc-hint">Aucun geste fait depuis Gestion pour l’instant.</p> : (
                <ol className="tb-journal">
                  {journal.map((g, i) => (
                    <li key={`${g.le}-${i}`}>
                      <span className="tb-mono tb-gris">{g.heure}</span>
                      <span className="tb-journal__quoi">{g.quoi}</span>
                      <span className="tb-gris">{g.qui ?? '—'}</span>
                      <span className={g.issue === 'ok' ? 'tb-ton--ok' : 'tb-ton--warn'}>{g.issue === 'ok' ? 'OK' : 'issue inconnue'}</span>
                    </li>
                  ))}
                </ol>
              )}
            </section>
          </section>

          <aside className="tb-fiche__cote" aria-label="Réglages">
            <Faits t={t} />
            {peut.piloter ? <Ecran t={t} onFait={apres} /> : null}
            {peut.piloter ? <Dashboard t={t} onFait={apres} /> : null}
            {peut.piloter ? <LectureSeule t={t} onFait={apres} /> : null}
            {peut.gerer ? <HorsService t={t} onFait={apres} /> : null}
            {peut.gerer ? <Maintenance t={t} onFait={apres} /> : null}
            {peut.wifi ? <WifiTablette t={t} onFait={apres} /> : null}
            {peut.gerer ? <Identite t={t} onFait={apres} /> : null}
            {peut.enroler ? <Retrait t={t} /> : null}
          </aside>
        </div>
        {annonce ? <AnnonceModale cible={{ type: 'tablette', tablette: t }} onFermer={() => { setAnnonce(false); apres() }} /> : null}
      </main>
    </Shell>
  )
}

type Peut = { piloter: boolean; gerer: boolean; enroler: boolean; wifi: boolean }
const base = (t: Tablette) => `/api/v1/tablettes/${encodeURIComponent(t.id)}`

function useGeste(onFait: () => void) {
  const toast = useToast()
  const [envoi, setEnvoi] = useState<string | null>(null)
  const faire = async (nom: string, method: 'POST' | 'PATCH' | 'DELETE', chemin: string, corps: unknown, succes: string): Promise<boolean> => {
    setEnvoi(nom)
    const r = await geste(method, chemin, corps)
    setEnvoi(null)
    toast(r.ok ? 'ok' : 'error', r.ok ? (r.message === 'Fait.' ? succes : r.message) : r.message)
    if (r.ok) onFait()
    return r.ok
  }
  return { envoi, faire }
}

function Ordres({ t, peut, onFait, onAnnonce }: { t: Tablette; peut: Peut; onFait: () => void; onAnnonce: () => void }) {
  const confirm = useConfirm()
  const { envoi, faire } = useGeste(onFait)
  const horsLigne = t.en_ligne === false
  return (
    <div className="tb-actions">
      {peut.piloter ? (
        <>
          <button type="button" className="lc-btn" disabled={!!envoi || horsLigne} onClick={() => { void faire('recharger', 'POST', `${base(t)}/ordre/recharger`, undefined, 'Page rechargée.') }}><RefreshCw size={16} aria-hidden="true" /> Recharger</button>
          <button type="button" className="lc-btn" disabled={!!envoi || horsLigne} onClick={() => { void faire('sonner', 'POST', `${base(t)}/ordre/sonner`, undefined, 'La tablette sonne.') }}><BellRing size={16} aria-hidden="true" /> Faire sonner</button>
          <button type="button" className="lc-btn" disabled={!!envoi} onClick={onAnnonce}><Megaphone size={16} aria-hidden="true" /> Annonce</button>
        </>
      ) : null}
      {peut.gerer ? (
        <button type="button" className="lc-btn lc-btn--danger" disabled={!!envoi || horsLigne} onClick={() => {
          void (async () => {
            if (!(await confirm({
              title: `Redémarrer « ${nomDe(t)} » ?`,
              message: 'La tablette s’éteint et revient en une minute environ. Son écran est indisponible pendant ce temps.',
              variant: 'sensitive', confirmLabel: 'Redémarrer',
            }))) return
            await faire('redemarrer', 'POST', `${base(t)}/ordre/redemarrer`, undefined, 'Redémarrage demandé : retour dans une minute environ.')
          })()
        }}><RotateCw size={16} aria-hidden="true" /> {envoi === 'redemarrer' ? 'Envoi…' : 'Redémarrer…'}</button>
      ) : null}
    </div>
  )
}

function Capture({ t, peutPiloter, onDemande }: { t: Tablette; peutPiloter: boolean; onDemande: () => void }) {
  const { envoi, faire } = useGeste(onDemande)
  const [sansImage, setSansImage] = useState(false)
  const url = urlCapture(t, 'pleine')
  useEffect(() => { setSansImage(false) }, [url])
  const quand = t.capture_le
  return (
    <div className="tb-capture">
      <div className="tb-capture__cadre">
        {!sansImage ? <img src={url} alt={`Écran de ${nomDe(t)}, capture de ${heure(quand, true)}`} onError={() => setSansImage(true)} />
          : <div className="tb-carte__veille">Pas de capture disponible</div>}
      </div>
      <div className="tb-capture__pied">
        <span className="lc-hint">Capture de {heure(quand, true)} · automatique toutes les 5 min</span>
        <div className="tb-actions">
          {peutPiloter ? (
            <button type="button" className="lc-btn" disabled={!!envoi || t.en_ligne === false} onClick={() => { void faire('capture', 'POST', `${base(t)}/ordre/capture`, undefined, 'Capture demandée.') }}>
              <Camera size={16} aria-hidden="true" /> {envoi ? 'Demande…' : 'Nouvelle capture'}
            </button>
          ) : null}
          <button type="button" className="lc-btn" disabled title="Prévue plus tard">Prendre la main</button>
        </div>
      </div>
    </div>
  )
}

function Faits({ t }: { t: Tablette }) {
  const e = t.etat ?? {}
  const b = batterie(t)
  if (!t.etat) return (
    <section className="lc-panel tb-panneau" aria-labelledby="tb-etat">
      <h2 className="tb-h2" id="tb-etat">État</h2>
      <p className="lc-hint">La tablette n’a pas encore publié d’état complet.</p>
    </section>
  )
  const lignes: [string, string, (string | undefined)?][] = [
    ['Batterie', b.texte, b.ton],
    ['Alimentation', e.branchee === true ? (e.en_charge ? 'Branchée, en charge' : 'Branchée') : e.branchee === false ? 'Débranchée' : '—', e.branchee === false ? 'warn' : undefined],
    ['Température', temperature(t), typeof e.temperature === 'number' && e.temperature >= 42 ? 'warn' : undefined],
    ['Wi-Fi', wifi(e.rssi)],
    ['Adresse', e.ip ?? '—'],
    ['Allumée depuis', duree(e.uptime_min)],
    ['Dernier toucher', ilYa(e.dernier_toucher)],
    ['WebView', e.webview ?? '—'],
    ['Compte Home Assistant', t.ha_compte ?? '—'],
    ['Lecture seule', e.lecture_seule?.actif ? 'Oui' : e.lecture_seule ? 'Non' : '—'],
  ]
  return (
    <section className="lc-panel tb-panneau" aria-labelledby="tb-etat">
      <h2 className="tb-h2" id="tb-etat">État</h2>
      <dl className="tb-faits">
        {lignes.map(([k, v, ton]) => <div key={k}><dt>{k}</dt><dd className={ton ? `tb-ton--${ton}` : undefined}>{v}</dd></div>)}
      </dl>
    </section>
  )
}

/** Un curseur qui n'envoie qu'une fois relâché (pas un ordre par pixel). */
function Curseur({ libelle, valeur, desactive, onEnvoi }: { libelle: string; valeur: number | null; desactive: boolean; onEnvoi: (v: number) => void }) {
  const [local, setLocal] = useState<number | null>(null)
  const affiche = local ?? valeur ?? 0
  const envoyer = () => { if (local !== null && local !== valeur) onEnvoi(local); setLocal(null) }
  const id = `tb-${libelle}`
  return (
    <div className="tb-curseur">
      <div className="tb-curseur__tete"><label htmlFor={id}>{libelle}</label><span className="tb-mono">{valeur === null && local === null ? '—' : `${affiche} %`}</span></div>
      <input id={id} type="range" min={0} max={100} step={5} value={affiche} disabled={desactive}
        onChange={(ev) => setLocal(Number(ev.target.value))} onPointerUp={envoyer} onKeyUp={envoyer} onBlur={envoyer} />
    </div>
  )
}

function Ecran({ t, onFait }: { t: Tablette; onFait: () => void }) {
  const { envoi, faire } = useGeste(onFait)
  const e = t.etat ?? {}
  const off = t.en_ligne === false
  const reg = (r: string, valeur: unknown, ok: string) => { void faire(r, 'POST', `${base(t)}/reglage/${r}`, { valeur }, ok) }
  const auto = e.luminosite === 'auto'
  return (
    <section className="lc-panel tb-panneau" aria-labelledby="tb-ecran">
      <h2 className="tb-h2" id="tb-ecran">Écran</h2>
      <div className="tb-ligne">
        <SegmentedControl value={e.ecran === 'OFF' ? 'OFF' : 'ON'} ariaLabel="Écran"
          options={[{ value: 'ON', label: 'Allumé' }, { value: 'OFF', label: 'Éteint' }]}
          onChange={(v) => { if (!off && !envoi) reg('ecran', v, v === 'ON' ? 'Écran allumé.' : 'Écran éteint.') }} />
        <Select compact ariaLabel="Méthode d’extinction" value={e.ecran_methode ?? 'A'}
          options={[{ value: 'A', label: 'A · vraie veille' }, { value: 'B', label: 'B · voile noir' }]}
          onChange={(v) => { if (!off) reg('ecran_methode', v, `Méthode ${v} retenue.`) }} />
      </div>
      <Curseur libelle="Luminosité" valeur={auto ? null : typeof e.luminosite === 'number' ? e.luminosite : null} desactive={off || auto || !!envoi}
        onEnvoi={(v) => reg('luminosite', v, `Luminosité ${v} %.`)} />
      <label className="tb-case"><input type="checkbox" checked={auto} disabled={off || !!envoi}
        onChange={(ev) => reg('luminosite', ev.target.checked ? 'auto' : 60, ev.target.checked ? 'Luminosité automatique.' : 'Luminosité 60 %.')} /> Luminosité automatique</label>
      <Curseur libelle="Volume" valeur={typeof e.volume === 'number' ? e.volume : null} desactive={off || !!envoi}
        onEnvoi={(v) => reg('volume', v, `Volume ${v} %.`)} />
    </section>
  )
}

function Dashboard({ t, onFait }: { t: Tablette; onFait: () => void }) {
  const { envoi, faire } = useGeste(onFait)
  const [liste, setListe] = useState<DashboardHa[] | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const actuel = t.etat?.dashboard ?? null
  const [choix, setChoix] = useState<string | null>(null)
  const dejaLu = useRef(false)
  useEffect(() => {
    if (dejaLu.current) return
    dejaLu.current = true
    void apiRead<unknown>('/api/v1/tablettes/dashboards').then((r) => {
      if (r.status === 200) setListe(dashboardsPour(dashboardsDe(r.data), t.ha_compte))
      else setErreur(typeof (r.data as { message?: unknown })?.message === 'string' ? (r.data as { message: string }).message : 'Liste de Home Assistant illisible.')
    }).catch(() => setErreur('Gestion n’a pas répondu.'))
  }, [t.ha_compte])
  const dashId = choix ?? actuel?.dashboard ?? liste?.[0]?.id ?? null
  const d = liste?.find((x) => x.id === dashId) ?? null
  const envoyer = (dashboard: string, vue: string, titre: string) => {
    void faire('dashboard', 'POST', `${base(t)}/reglage/dashboard`, { valeur: { dashboard, vue } }, `Affiché : ${titre}. Nouvelle capture dans un instant.`)
      .then((ok) => { if (ok) setChoix(null) })
  }
  return (
    <section className="lc-panel tb-panneau" aria-labelledby="tb-dash">
      <div className="tb-h2-ligne"><h2 className="tb-h2" id="tb-dash">Dashboard affiché</h2><span className="lc-hint">liste lue dans Home Assistant</span></div>
      {erreur ? <p className="lc-hint" role="status">{erreur}</p> : liste === null ? <p className="lc-hint">Lecture de la liste…</p> : liste.length === 0 ? <p className="lc-hint">Home Assistant ne propose aucun dashboard à cette tablette.</p> : (
        <>
          <div className="tb-dashs" role="group" aria-label="Dashboard">
            {liste.map((x) => (
              <button key={x.id} type="button" className={`tb-dash${x.id === dashId ? ' tb-dash--on' : ''}`} aria-pressed={x.id === dashId} onClick={() => setChoix(x.id)}>
                <span className="tb-dash__icone" aria-hidden="true"><LayoutDashboard size={18} /></span>
                <span>{x.titre}<span className="tb-dash__n">{x.vues.length} vue{x.vues.length > 1 ? 's' : ''}</span></span>
              </button>
            ))}
          </div>
          {d ? (
            <div className="tb-chips" role="group" aria-label="Vue">
              {d.vues.map((v) => {
                const courante = actuel?.dashboard === d.id && actuel?.vue === v.id
                return (
                  <button key={v.id} type="button" className={`tb-chip${courante ? ' tb-chip--on' : ''}`} aria-pressed={courante}
                    disabled={!!envoi || t.en_ligne === false || courante} onClick={() => envoyer(d.id, v.id, `${d.titre} › ${v.titre}`)}>{v.titre}</button>
                )
              })}
            </div>
          ) : null}
          <p className="lc-hint">{actuel?.dashboard ? `Affiché : ${liste.find((x) => x.id === actuel.dashboard)?.titre ?? actuel.dashboard} › ${actuel.vue ?? '—'}` : 'Dashboard actuel inconnu.'}</p>
        </>
      )}
    </section>
  )
}

function HorsService({ t, onFait }: { t: Tablette; onFait: () => void }) {
  const confirm = useConfirm()
  const { envoi, faire } = useGeste(onFait)
  const bloque = t.etat?.bloque === true
  const [style, setStyle] = useState<StyleBlocage>(t.etat?.bloque_style ?? 'hors_service')
  const [texte, setTexte] = useState(t.etat?.bloque_texte ?? '')
  const parDefaut = STYLES_BLOCAGE.find((x) => x.value === style)?.texteParDefaut ?? ''
  return (
    <section className="lc-panel tb-panneau" aria-labelledby="tb-hs">
      <h2 className="tb-h2" id="tb-hs">Bloquer la tablette</h2>
      {bloque ? <p className="tb-bandeau" role="status">{libelleBlocage(t.etat?.bloque_style)}{t.etat?.bloque_texte ? ` · « ${t.etat.bloque_texte} »` : ''}</p> : null}
      <SegmentedControl value={style} ariaLabel="Apparence du blocage" size="sm"
        options={STYLES_BLOCAGE.map((x) => ({ value: x.value, label: x.label }))} onChange={setStyle} />
      <Field label="Message affiché" helper={parDefaut ? `Vide : « ${parDefaut} ». ${texte.length} / 140` : `Vide : cadenas seul. ${texte.length} / 140`}>
        <TextInput value={texte} onChange={setTexte} maxLength={140} ariaLabel="Message affiché pendant le blocage" />
      </Field>
      <div className="tb-actions">
        <button type="button" className="lc-btn lc-btn--danger" disabled={!!envoi} onClick={() => {
          void (async () => {
            if (!(await confirm({ title: `Bloquer « ${nomDe(t)} » ?`, message: 'La tablette n’affiche plus que l’écran de blocage, jusqu’à la remise en service. Hors ligne, elle se bloquera à son retour.', variant: 'sensitive', confirmLabel: 'Bloquer' }))) return
            await faire('bloque', 'POST', `${base(t)}/reglage/bloque`, { valeur: { actif: true, style, ...(texte.trim() ? { texte: texte.trim() } : {}) } }, 'Tablette bloquée.')
          })()
        }}>{bloque ? 'Changer le blocage' : 'Bloquer'}</button>
        {bloque ? <button type="button" className="lc-btn" disabled={!!envoi} onClick={() => { void faire('bloque', 'POST', `${base(t)}/reglage/bloque`, { valeur: { actif: false } }, 'Tablette remise en service.') }}>Remettre en service</button> : null}
      </div>
    </section>
  )
}

/** Lecture seule : la tablette affiche sans qu'on puisse la toucher, éventuellement sur une vue imposée. */
function LectureSeule({ t, onFait }: { t: Tablette; onFait: () => void }) {
  const { envoi, faire } = useGeste(onFait)
  const ls = t.etat?.lecture_seule ?? null
  const [vue, setVue] = useState('')
  const [liste, setListe] = useState<DashboardHa[] | null>(null)
  useEffect(() => {
    let vivant = true
    void apiRead<unknown>('/api/v1/tablettes/dashboards').then((r) => { if (vivant && r.status === 200) setListe(dashboardsPour(dashboardsDe(r.data), t.ha_compte)) }).catch(() => undefined)
    return () => { vivant = false }
  }, [t.ha_compte])
  const options = [{ value: '', label: 'Sa vue habituelle' }, ...(liste ?? []).flatMap((d) => d.vues.map((v) => ({ value: `${d.id}|${v.id}`, label: `${d.titre} › ${v.titre}` })))]
  const activer = () => {
    const [dashboard, v] = vue ? vue.split('|') : []
    void faire('lecture_seule', 'POST', `${base(t)}/reglage/lecture_seule`, { valeur: { actif: true, ...(dashboard && v ? { dashboard: { dashboard, vue: v } } : {}) } }, 'Lecture seule activée.')
  }
  return (
    <section className="lc-panel tb-panneau" aria-labelledby="tb-ls">
      <h2 className="tb-h2" id="tb-ls">Lecture seule</h2>
      <p className="lc-hint">{ls?.actif ? `Active${ls.dashboard ? ` sur ${ls.dashboard.dashboard} › ${ls.dashboard.vue}` : ''} : l’écran affiche, les touchers sont ignorés.` : 'L’écran affiche, mais les touchers sont ignorés.'}</p>
      {!ls?.actif ? <Select value={vue} options={options} onChange={setVue} ariaLabel="Vue affichée pendant la lecture seule" /> : null}
      <div className="tb-actions">
        {ls?.actif
          ? <button type="button" className="lc-btn" disabled={!!envoi} onClick={() => { void faire('lecture_seule', 'POST', `${base(t)}/reglage/lecture_seule`, { valeur: { actif: false } }, 'Lecture seule terminée.') }}>Terminer la lecture seule</button>
          : <button type="button" className="lc-btn" disabled={!!envoi} onClick={activer}>Passer en lecture seule</button>}
      </div>
    </section>
  )
}

function Maintenance({ t, onFait }: { t: Tablette; onFait: () => void }) {
  const { envoi, faire } = useGeste(onFait)
  const enCours = enMaintenance(t)
  const lancer = (minutes: number) => { void faire('maintenance', 'POST', `${base(t)}/maintenance`, { minutes }, minutes ? `Maintenance ${minutes} min.` : 'Maintenance terminée.') }
  return (
    <section className="lc-panel tb-panneau" aria-labelledby="tb-maint">
      <h2 className="tb-h2" id="tb-maint">Maintenance</h2>
      <p className="lc-hint">{enCours ? `En cours jusqu’à ${heure(t.etat?.maintenance_fin)}.` : 'Suspend les alertes de cette tablette et affiche « Maintenance en cours ».'}</p>
      <div className="tb-actions">
        {[15, 30, 60, 120].map((m) => <button key={m} type="button" className="lc-btn lc-btn--sm" disabled={!!envoi || t.en_ligne === false} onClick={() => lancer(m)}>{m < 60 ? `${m} min` : `${m / 60} h`}</button>)}
        {enCours ? <button type="button" className="lc-btn lc-btn--sm" disabled={!!envoi} onClick={() => lancer(0)}>Terminer</button> : null}
      </div>
    </section>
  )
}

/**
 * Wi-Fi : un balayage à la demande (la tablette allume brièvement la
 * localisation, 15 s environ), puis Apprendre un profil de kiosk-api qui porte
 * le même SSID, ou Oublier un réseau connu (jamais celui en cours). Aucun mot
 * de passe ne passe par Gestion.
 */
function WifiTablette({ t, onFait }: { t: Tablette; onFait: () => void }) {
  const confirm = useConfirm()
  const toast = useToast()
  const [scan, setScan] = useState<ScanWifi | null>(null)
  const [profils, setProfils] = useState<ProfilWifi[] | null>(null)
  const [envoi, setEnvoi] = useState<string | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  useEffect(() => {
    let vivant = true
    void apiRead<unknown>('/api/v1/tablettes/wifi/profils').then((r) => { if (vivant) setProfils(r.status === 200 ? profilsWifiDe(r.data) : []) }).catch(() => { if (vivant) setProfils([]) })
    return () => { vivant = false }
  }, [])
  const off = t.en_ligne === false
  const balayer = async () => {
    setEnvoi('scan'); setErreur(null)
    const r = await geste('POST', `${base(t)}/wifi/scan`, {})
    setEnvoi(null)
    const lu = r.ok ? scanWifiDe(r.data) : null
    if (lu) setScan(lu)
    else setErreur(r.ok ? 'Réponse de la tablette illisible.' : r.message)
  }
  const agir = async (nom: string, chemin: string, corps: unknown, succes: string) => {
    setEnvoi(nom)
    const r = await geste('POST', chemin, corps)
    setEnvoi(null)
    toast(r.ok ? 'ok' : 'error', r.ok ? succes : r.message)
    if (r.ok) { onFait(); await balayer() }
  }
  const apprendre = async (p: ProfilWifi) => {
    if (!(await confirm({
      title: `Apprendre « ${p.nom} » à « ${nomDe(t)} » ?`,
      message: 'La tablette enregistre ce réseau avec sa vraie adresse MAC et le rejoindra quand il sera à portée. Elle garde son Wi-Fi actuel. Le mot de passe va de kiosk-api à la tablette, sans passer par Gestion.',
      variant: 'sensitive', confirmLabel: 'Apprendre',
    }))) return
    await agir(`apprendre:${p.nom}`, `${base(t)}/wifi/apprendre`, { profil: p.nom }, `Réseau « ${p.ssid ?? p.nom} » appris.`)
  }
  const oublier = async (ssid: string) => {
    if (!(await confirm({
      title: `Oublier « ${ssid} » ?`,
      message: 'La tablette retire ce réseau de sa mémoire et ne s’y connectera plus. Pour le retrouver, il faudra le lui apprendre de nouveau.',
      variant: 'sensitive', confirmLabel: 'Oublier',
    }))) return
    await agir(`oublier:${ssid}`, `${base(t)}/wifi/oublier`, { ssid }, `Réseau « ${ssid} » oublié.`)
  }
  const lignes = scan ? lignesWifi(scan) : []
  return (
    <section className="lc-panel tb-panneau" aria-labelledby="tb-wifi">
      <div className="tb-h2-ligne"><h2 className="tb-h2" id="tb-wifi">Wi-Fi</h2>{scan && !scan.frais ? <span className="lc-hint">liste du balayage précédent</span> : null}</div>
      <p className="lc-hint">{scan ? (scan.actuel ? <>Connectée à <strong className="tb-wifi__ssid">{scan.actuel}</strong> · {wifi(t.etat?.rssi)}</> : 'Réseau actuel inconnu.') : `Signal actuel : ${wifi(t.etat?.rssi)}. Balayez pour voir les réseaux que la tablette capte.`}</p>
      {erreur ? <p className="tb-bandeau" role="status">{erreur}</p> : null}
      {scan ? (
        lignes.length === 0 ? <p className="lc-hint">Aucun réseau vu ni connu.</p> : (
          <ul className="tb-wifi" aria-label="Réseaux Wi-Fi">
            {lignes.map((l) => {
              const profil = profils ? profilPour(l.ssid, profils) : null
              const peutApprendre = !!profil && !l.actuel && !!l.visible && (!l.connu || l.connu.appris_par_app)
              return (
                <li key={l.ssid} className={`tb-wifi__ligne${l.actuel ? ' tb-wifi__ligne--actuel' : ''}`}>
                  <span className={`tb-barres tb-barres--${l.visible ? barresSignal(l.visible.signal_dbm) : 0}`} role="img"
                    aria-label={l.visible ? `Signal ${l.visible.signal_dbm} dBm` : 'Hors de portée'} title={l.visible ? `${l.visible.signal_dbm} dBm` : 'Hors de portée'}>
                    <i /><i /><i /><i />
                  </span>
                  <span className="tb-wifi__nom">
                    <span className="tb-wifi__ssid">{l.ssid}</span>
                    <span className="tb-wifi__detail">
                      {l.visible ? <>{l.visible.securite !== 'ouvert' ? <Lock size={12} aria-hidden="true" /> : null}{libelleSecurite(l.visible.securite)}{l.visible.bandes.length ? ` · ${l.visible.bandes.join(', ')}` : ''}</> : 'hors de portée'}
                      {l.connu ? (l.connu.mac === 'aleatoire'
                        ? <span className="tb-ton--warn" title="La réservation DHCP et Home Assistant ne reconnaîtront pas la tablette sur ce réseau."> · MAC aléatoire</span>
                        : l.connu.mac === 'reelle' ? ' · MAC réelle' : '') : null}
                    </span>
                  </span>
                  <span className="tb-wifi__badges">
                    {l.actuel ? <span className="tb-badge tb-badge--on">actuel</span> : l.connu ? <span className="tb-badge">connu</span> : null}
                  </span>
                  <span className="tb-actions">
                    {peutApprendre ? <button type="button" className="lc-btn lc-btn--sm" disabled={!!envoi || off} onClick={() => { void apprendre(profil) }}>{envoi === `apprendre:${profil.nom}` ? 'Envoi…' : l.connu ? 'Mettre à jour' : 'Apprendre'}</button> : null}
                    {l.connu && !l.actuel ? <button type="button" className="lc-btn lc-btn--sm lc-btn--danger" disabled={!!envoi || off} onClick={() => { void oublier(l.ssid) }}>{envoi === `oublier:${l.ssid}` ? 'Envoi…' : 'Oublier…'}</button> : null}
                  </span>
                </li>
              )
            })}
          </ul>
        )
      ) : null}
      {profils && profils.length === 0 ? <p className="lc-hint">kiosk-api ne propose encore aucun profil Wi-Fi à apprendre.</p> : null}
      <div className="tb-actions">
        <button type="button" className="lc-btn" disabled={!!envoi || off} onClick={() => { void balayer() }}>
          <Wifi size={16} aria-hidden="true" /> {envoi === 'scan' ? 'Balayage… (15 s environ)' : scan ? 'Balayer de nouveau' : 'Balayer'}
        </button>
      </div>
    </section>
  )
}

function Identite({ t, onFait }: { t: Tablette; onFait: () => void }) {
  const confirm = useConfirm()
  const { envoi, faire } = useGeste(onFait)
  const [nom, setNom] = useState(t.nom ?? '')
  const [salle, setSalle] = useState(t.salle ?? '')
  const change = (nom.trim() && nom.trim() !== (t.nom ?? '')) || (salle && salle !== (t.salle ?? ''))
  return (
    <section className="lc-panel tb-panneau" aria-labelledby="tb-id">
      <h2 className="tb-h2" id="tb-id">Nom et emplacement</h2>
      <Field label="Nom"><TextInput value={nom} onChange={setNom} maxLength={60} ariaLabel="Nom de la tablette" /></Field>
      <Field label="Emplacement">
        <Select value={salle} placeholder="Choisir" options={SALLES.map((s) => ({ value: s, label: s }))} onChange={setSalle} ariaLabel="Emplacement" />
      </Field>
      <div className="tb-actions">
        <button type="button" className="lc-btn lc-btn--primary" disabled={!change || !!envoi} onClick={() => {
          const corps: Record<string, string> = {}
          if (nom.trim() !== (t.nom ?? '')) corps['nom'] = nom.trim()
          if (salle && salle !== (t.salle ?? '')) corps['salle'] = salle
          void faire('identite', 'PATCH', base(t), corps, 'Fiche enregistrée.')
        }}>Enregistrer</button>
        <button type="button" className="lc-btn" disabled={!!envoi || t.en_ligne === false} onClick={() => {
          void (async () => {
            if (!(await confirm({ title: 'Mettre à jour l’app maintenant ?', message: 'La tablette installe la dernière version de l’app et redémarre son affichage (moins d’une minute).', variant: 'standard', confirmLabel: 'Mettre à jour' }))) return
            await faire('maj', 'POST', `${base(t)}/ordre/maj`, undefined, 'Mise à jour demandée.')
          })()
        }}>Mettre à jour l’app ({t.etat?.app ?? '—'})</button>
      </div>
    </section>
  )
}

function Retrait({ t }: { t: Tablette }) {
  const confirm = useConfirm()
  const toast = useToast()
  const [envoi, setEnvoi] = useState(false)
  return (
    <section className="lc-panel tb-panneau tb-panneau--danger" aria-labelledby="tb-retrait">
      <h2 className="tb-h2" id="tb-retrait">Retirer la tablette</h2>
      <p className="lc-hint">Son accès au broker est révoqué : elle ne reçoit plus d’ordres. Pour la réutiliser, il faudra l’enrôler de nouveau.</p>
      <button type="button" className="lc-btn lc-btn--danger" disabled={envoi} onClick={() => {
        void (async () => {
          if (!(await confirm({ title: `Retirer « ${nomDe(t)} » ?`, message: 'La tablette ne sera plus pilotable depuis Gestion ni Home Assistant tant qu’elle n’est pas enrôlée de nouveau.', variant: 'critical', confirmLabel: 'Retirer' }))) return
          setEnvoi(true)
          const r = await geste('DELETE', base(t))
          setEnvoi(false)
          if (r.ok) { toast('ok', `« ${nomDe(t)} » retirée.`); navigate(CHEMIN_TABLETTES) } else toast('error', r.message)
        })()
      }}><Trash2 size={16} aria-hidden="true" /> {envoi ? 'Retrait…' : 'Retirer…'}</button>
    </section>
  )
}
